import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {NgClass} from '@angular/common';
import {TagStateEnum} from '../../enums/tag-state.enum';
import {ActivatedRoute} from '@angular/router';
import {Member, MemberHal, State} from '../../models/HAL/member-hal.model';
import {HttpClient} from '@angular/common/http';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DialogService} from '../../services/dialog.service';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatFormField, MatFormFieldModule} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {SnackBarService} from '../../services/snackbar.service';
import {
  DynamicConfirmationDialogComponent
} from '../../components/dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {AddMemberComponent} from '../../components/add-member/add-member.component';
import {debounceTime} from 'rxjs';

@Component({
  selector: 'app-member-overview',
  imports: [
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatButton,
    MatIconButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatFormFieldModule,
    MatTableModule,
    MatProgressSpinner,
    NgClass
  ],
  templateUrl: './member-overview.component.html',
  styleUrl: './member-overview.component.scss'
})
export class MemberOverviewComponent implements OnInit, AfterViewInit {

  protected readonly displayedColumns: string[] = ['email', 'role', 'state', 'action'];
  protected dataSource: MatTableDataSource<Member> = new MatTableDataSource<Member>([]);
  protected memberHal!: MemberHal;
  protected searchMemberShipsControl: FormControl;
  protected loadingMembers: boolean = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(readonly activatedRoute: ActivatedRoute,
              private readonly http: HttpClient,
              private readonly loadingService: LoadingSpinnerService,
              private readonly dialogService: DialogService,
              private readonly snackBarService: SnackBarService,
              private readonly translateService: TranslateService) {
    this.memberHal = new MemberHal(activatedRoute.snapshot.data['memberHal']);
    this.dataSource = new MatTableDataSource<Member>(this.memberHal._embedded?.members);
    this.searchMemberShipsControl = new FormControl({value: '', disabled: false});
  }

  ngOnInit(): void {
    this.searchMemberShipsControl.valueChanges.pipe(
      debounceTime(250)
    ).subscribe(() => {
      this.updateMemberHal(this.paginator);
    });
  }

  ngAfterViewInit(): void {
    this.loadingService.hide();
  }

  clearSearch(input?: HTMLInputElement) {
    this.searchMemberShipsControl.setValue('');
    setTimeout(() => input?.focus(), 0);
  }

  protected getStatus(state: State): TagStateEnum {
    switch (state) {
      case State.ACTIVE:
        return TagStateEnum.SUCCESS;
      case State.PENDING:
        return TagStateEnum.PENDING;
    }
  }

  protected isActive(member: Member): boolean {
    return member.state === State.ACTIVE;
  }

  protected updateMemberHal(pageEvent: PageEvent): void {
    this.loadingMembers = true;
    const url = new URL(this.memberHal._links.getLink('self')!.href);
    url.searchParams.set('size', pageEvent.pageSize.toString());
    url.searchParams.set('page', pageEvent.pageIndex.toString());
    url.searchParams.set('searchParameter', this.searchMemberShipsControl.value);
    this.http.get<MemberHal>(url.href).subscribe({
      next: (newHal) => {
        this.memberHal = new MemberHal(newHal);
        this.dataSource = new MatTableDataSource<Member>(this.memberHal._embedded?.members);
        this.loadingMembers = false;
      },
      error: () => {
        this.snackBarService.open('memberOverview.snackbars.loadError', 3, 'error');
        this.loadingMembers = false;
      }
    });
  }

  protected openInviteMemberDialog() {
    const dialogRef = this.dialogService.open(AddMemberComponent, {
      width: '528px',
    });
    dialogRef.afterClosed().subscribe(email => {
      if (email) {
        this.invite(email);
      }
    });
  }

  protected openRemoveMemberDialog(member: Member) {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'memberOverview.removeMemberConfirmation.title',
        message: this.translateService.instant('memberOverview.removeMemberConfirmation.message', {mail: member.email}),
        confirmButtonText: 'memberOverview.removeMemberConfirmation.confirm',
        cancelButtonText: 'memberOverview.removeMemberConfirmation.cancel'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.removeMembership(member);
      }
    });
  }

  private invite(emailAddress: string) {
    this.loadingMembers = true;
    const links = this.memberHal._links;
    const url = links.getLink('create')!.href;
    const data = JSON.parse('{}');
    data.email = emailAddress;
    data.role = "MEMBER";
    this.http.post(url, data).subscribe({
      next: () => {
        this.updateMemberHal(this.paginator);
        this.snackBarService.open('memberOverview.snackbars.inviteSuccess', 3);
      },
      error: () => {
        this.updateMemberHal(this.paginator);
        this.snackBarService.open('memberOverview.snackbars.inviteError', 3, 'error');
      }
    });
  }

  private removeMembership(member: Member) {
    this.loadingMembers = true;
    const deleteUrl = member._links.getLink('delete')!.href;
    this.http.delete(deleteUrl).subscribe({
      next: () => {
        this.updateMemberHal(this.paginator);
        this.snackBarService.open('memberOverview.snackbars.removedSuccess', 3);
      },
      error: () => {
        this.updateMemberHal(this.paginator);
        this.snackBarService.open('memberOverview.snackbars.removedError', 3, 'error');
      }
    });
  }
}
